import { FunctionComponent } from 'react'
import DromoUploader from 'dromo-uploader-react'
import {
  IReviewStepData,
  IRowHookInput,
  IRowHookOutputInternal,
  IBeforeFinishOutput,
} from 'dromo-uploader-js'
import {
  IPublicConnectionMethods,
  IRowToAdd,
} from 'dromo-uploader-js/dist/interfaces'
import {
  fieldSchema,
  qbRecordIdSchema,
  validations,
} from '../helpers/SiteListUpload/FieldSchema'
import { DromoSiteRecord } from 'common/types'
import { addColumnsIfNeeded } from '../helpers/DromoUploader/addColumnsIfNeeded'
import { parseDateValues } from '../helpers/DromoUploader/dateValidation'
import { addZipCodeLeadingZeros } from '../helpers/DromoUploader/zipCodeValidation'
import { convertZeroToNull } from '../helpers/DromoUploader/convertZeroToNull'
import { validateNumbers } from '../helpers/DromoUploader/validateNumbers'
import { updateCellMessages } from '../helpers/DromoUploader/updateCellMessages'
import { handleBulkProcessing } from '../helpers/SiteListUpload/HandleBulkProcessing'
import { getUploadSummary } from '../helpers/DromoUploader/getUploadSummary'
import { checkForDuplicates } from '../helpers/SiteListUpload/checkForDuplicates'
import {
  chunkSiteListUpload,
  UpsertCounts,
} from '../helpers/DromoUploader/chunkSiteListUpload'
import axios from 'axios'
import { IAuthUser, useIDToken } from '../../state/modules/auth'
import { getAPI } from '../../utils/api'
import { useImpersonatingUserOrUser } from '../../state/modules/auth'
import {
  Auth0Role,
  SiteListType,
  requestLimit,
  geocodingRequestsPerMinuteLimit,
} from '@black-bear-energy/black-bear-energy-common'
import { isProductionHost } from '../../utils/enviroment'
import { DuplicateCheckResult } from '../helpers/DromoUploader/Deduplication.consts'
import { getSiteUploadRecordList } from 'components/helpers/SiteListUpload/getSiteUploadRecordList'

interface SiteListUploaderProps {
  clientId?: number
  siteListType: SiteListType
  isOpen: boolean
  onUploadStart: () => void
  onUploadError: (message?: string) => void
  onClose: (counts?: UpsertCounts) => void
}

export const SiteListUploader: FunctionComponent<SiteListUploaderProps> = (
  props
) => {
  const {
    clientId,
    siteListType,
    isOpen,
    onUploadStart,
    onUploadError,
    onClose,
  } = props
  const user = useImpersonatingUserOrUser() as IAuthUser
  const accessToken = useIDToken()
  let soldRecordRows: IRowToAdd[]
  let updatedRecordRows: IRowHookOutputInternal[]
  let isManualEntry = false

  async function beforeUpload(
    sites: DromoSiteRecord[],
    instance: IPublicConnectionMethods,
    clientId?: number,
    accessToken?: string
  ): Promise<IBeforeFinishOutput> {
    if (!clientId) {
      return { cancel: true, message: 'No client selected' }
    }

    const api = getAPI(accessToken)

    let duplicatesResult: DuplicateCheckResult | null
    try {
      duplicatesResult = await checkForDuplicates(sites, clientId, api)
    } catch (err) {
      return {
        cancel: true,
        message:
          'An error was thrown while deduplicating records. Please contact support.',
      }
    }

    if (duplicatesResult === null) {
      // no duplicates - show upload summary
      const siteActions = sites.map((site) => site.siteAction)
      const didConfirm = confirm(getUploadSummary(siteActions))
      if (didConfirm) {
        return
      }
      return {
        cancel: true,
        message: '',
      }
    }

    // move duplicate rows to the top of the table
    await instance.removeRows(duplicatesResult.rowIdsToRemove)
    await instance.addRows(duplicatesResult.rowsToAdd)

    return {
      cancel: true,
      message: `${duplicatesResult.dupCount} record${
        duplicatesResult.dupCount > 1 ? 's are duplicates' : ' is a duplicate'
      } of other records. Please review and either update the records to be unique or choose Ignore as the site action.`,
    }
  }

  async function uploadSiteListData(
    data: DromoSiteRecord[],
    clientId?: number,
    accessToken?: string
  ): Promise<void> {
    if (!clientId) {
      throw new Error('No client selected')
    }
    if (data.length === 0) {
      updatedRecordRows = []
      onClose()
    }

    onUploadStart()

    const api = getAPI(accessToken)
    const sites = getSiteUploadRecordList(data, clientId)

    let counts: UpsertCounts

    try {
      counts = await chunkSiteListUpload(sites, siteListType, '/sites', api)
    } catch (error) {
      const description =
        axios.isAxiosError(error) &&
        typeof (error.response?.data as Record<string, unknown>)
          ?.description === 'string'
          ? ((error.response?.data as Record<string, unknown>)
              .description as string)
          : undefined
      onUploadError(description)
      return
    }

    updatedRecordRows = []
    onClose(counts)
  }

  async function onBulkRowHook(
    records: IRowHookInput[],
    mode: 'init' | 'update'
  ): Promise<IRowHookOutputInternal[]> {
    if (!clientId) {
      throw new Error('No client selected')
    }
    const isUpdate = mode === 'update'
    if (!isUpdate && isManualEntry && updatedRecordRows.length) {
      return updatedRecordRows
    }

    const api = getAPI(accessToken)

    const { newRows, updatedRows } = await handleBulkProcessing({
      records,
      isUpdate,
      siteListType,
      clientId,
      api,
    })
    soldRecordRows = newRows
    updatedRecordRows = updatedRows
    return updatedRows
  }

  // super admins can map to the 'QBRecordId' value
  const fields =
    user.role === Auth0Role.SUPER_ADMIN
      ? fieldSchema.concat(qbRecordIdSchema)
      : fieldSchema

  return (
    <DromoUploader
      licenseKey={process.env.REACT_APP_DROMO_FRONTEND_API_KEY!}
      fields={fields}
      settings={{
        importIdentifier: 'Site List Uploader',
        title: 'Site List Uploader',
        developmentMode: !isProductionHost(),
        maxFileSize: requestLimit,
        maxRecords: geocodingRequestsPerMinuteLimit,
        reviewStep: {
          processingText:
            'Geocoding addresses and comparing new sites with existing sites. This may take a while.',
          helpText:
            'Only rows without errors will be uploaded. Hover over a colored cell to see more information. If some errors cannot be resolved, click the "Export" button to download "Only rows with errors" and review this list with the client. Sites that are potentially sold are pulled from the database and added to the bottom of the sheet as new rows.',
        },
      }}
      user={{
        id: user.id ?? '',
        name: user.name,
        email: user.email,
        companyId:
          user.relatedEntity?.toString() ??
          user.relatedChannelPartner?.toString() ??
          '',
      }}
      columnHooks={[
        {
          fieldName: 'zipCode',
          callback: (values) => addZipCodeLeadingZeros(values),
        },
        {
          fieldName: 'acquireDate',
          callback: (values) => parseDateValues(values, true),
        },
        {
          fieldName: 'buildingBuildYear',
          callback: (values) => parseDateValues(values, false),
        },
        {
          fieldName: 'roofInstallYear',
          callback: (values) => parseDateValues(values, false),
        },
        {
          fieldName: 'buildingArea',
          callback: (values) => convertZeroToNull(values),
        },
        {
          fieldName: 'latitude',
          callback: (values) => validateNumbers(values),
        },
        {
          fieldName: 'longitude',
          callback: (values) => validateNumbers(values),
        },
      ]}
      stepHooks={[
        {
          type: 'REVIEW_STEP',
          callback: (instance, previewData) => {
            const reviewData = previewData as IReviewStepData
            // Manual entry has no file and therefore no header mapping.
            isManualEntry = Object.values(reviewData.headerMapping).length === 0
            return addColumnsIfNeeded(
              instance,
              previewData as IReviewStepData,
              []
            )
          },
        },
        {
          type: 'REVIEW_STEP_POST_HOOKS',
          callback: async (instance) => {
            await instance.addRows(soldRecordRows)
          },
        },
      ]}
      rowHooks={[(record) => updateCellMessages(record, validations)]}
      bulkRowHooks={[(records, mode) => onBulkRowHook(records, mode)]}
      onResults={(response: DromoSiteRecord[]) => {
        uploadSiteListData(response, clientId, accessToken).catch(() => {
          throw new Error('uploadSiteListData failed')
        })
      }}
      beforeFinish={(records, metadata, instance) =>
        beforeUpload(
          records as DromoSiteRecord[],
          instance,
          clientId,
          accessToken
        )
      }
      open={isOpen}
      onCancel={() => {
        updatedRecordRows = []
        onClose()
      }}
    ></DromoUploader>
  )
}
